export const DEAL_FILTERS = {
  ALL: 'all',
  FEATURED: 'featured',
  ENDING_SOON: 'ending-soon',
};

export const DEAL_SORTS = {
  FEATURED: 'featured',
  NEWEST: 'newest',
  ENDING_SOON: 'ending-soon',
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function parseDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function getOfferIdentifier(offer) {
  return String(offer?.slug || '').trim();
}

export function getOfferHref(offer) {
  const identifier = getOfferIdentifier(offer);
  return identifier ? `/offers/${encodeURIComponent(identifier)}` : '/offers';
}

export function getOfferImageUrl(offer) {
  return String(
    offer?.imageUrl ||
    offer?.coverImage?.publicUrl ||
    offer?.image ||
    offer?.listing?.imageUrl ||
    offer?.listing?.image ||
    ''
  ).trim();
}

export function getListingName(offer) {
  return String(offer?.listing?.name || offer?.businessName || offer?.merchantName || 'Local Business').trim();
}

export function getOfferValueLabel(offer) {
  return String(offer?.offerType || 'Special offer').replaceAll('_', ' ').trim();
}

export function isFeaturedOffer(offer) {
  return (Array.isArray(offer?.placements) ? offer.placements : []).some((placement) => placement?.status === 'ACTIVE' && placement?.isFeatured === true);
}

export function isEndingSoon(offer, now = new Date()) {
  const end = parseDate(offer?.endAt || offer?.endDate || offer?.expiresAt || offer?.validUntil);
  if (!end) return false;
  const timeLeft = end.getTime() - now.getTime();
  return timeLeft >= 0 && timeLeft <= 7 * DAY_MS;
}

export function formatOfferExpiration(value, locale) {
  const end = parseDate(value);
  if (!end) return 'No expiration listed';
  return `Ends ${end.toLocaleDateString(locale || 'en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })}`;
}

export function offerMatchesSearch(offer, query) {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return true;

  const haystack = [
    offer?.title,
    offer?.shortDescription,
    offer?.description,
    getListingName(offer),
  ]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');

  return haystack.includes(normalizedQuery);
}

export function filterOffers(offers, { filter = DEAL_FILTERS.ALL, searchQuery = '', now = new Date() } = {}) {
  return (Array.isArray(offers) ? offers : []).filter((offer) => {
    if (!offerMatchesSearch(offer, searchQuery)) return false;
    if (filter === DEAL_FILTERS.FEATURED) return isFeaturedOffer(offer);
    if (filter === DEAL_FILTERS.ENDING_SOON) return isEndingSoon(offer, now);
    return true;
  });
}

function getDateTime(value) {
  return parseDate(value)?.getTime() || 0;
}

function getEndingSoonSortValue(offer) {
  return parseDate(offer?.endAt || offer?.endDate || offer?.expiresAt || offer?.validUntil)?.getTime() || Number.POSITIVE_INFINITY;
}

export function sortOffers(offers, sort = DEAL_SORTS.FEATURED) {
  const rows = [...(Array.isArray(offers) ? offers : [])];

  return rows.sort((a, b) => {
    if (sort === DEAL_SORTS.NEWEST) {
      return getDateTime(b.publishedAt || b.createdAt) - getDateTime(a.publishedAt || a.createdAt);
    }

    if (sort === DEAL_SORTS.ENDING_SOON) {
      return getEndingSoonSortValue(a) - getEndingSoonSortValue(b);
    }

    const featuredDelta = Number(isFeaturedOffer(b)) - Number(isFeaturedOffer(a));
    if (featuredDelta !== 0) return featuredDelta;
    return getDateTime(b.publishedAt || b.createdAt) - getDateTime(a.publishedAt || a.createdAt);
  });
}

export function selectDeals(offers, options = {}) {
  return sortOffers(filterOffers(offers, options), options.sort);
}
